import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, Heart, Activity, Moon, Play, 
  Timer, Info, ChevronRight, Sparkles,
  Cloud, Wind, Waves, Music
} from 'lucide-react';
import { useHealthMetrics } from '../hooks/useHealthMetrics';
import { MindfulBreathing } from '../components/mindfulness/MindfulBreathing';
import { QuickFix } from '../components/mindfulness/QuickFix';
import { SoundTherapy } from '../components/mindfulness/SoundTherapy';
import { GuidedExperience } from '../components/mindfulness/GuidedExperience';

const exercises = [
  {
    id: 'breathing',
    name: 'Mindful Breathing',
    description: 'Guided breathing exercises for relaxation and focus',
    icon: <Wind className="w-6 h-6" />,
    color: 'from-primary/40 to-purple-500/40',
    duration: '5-15 min',
    component: MindfulBreathing,
    ambientColor: 'from-primary/5 via-purple-400/5 to-transparent'
  },
  {
    id: 'quickfix',
    name: 'Quick Fix',
    description: 'Interactive exercises to quickly reduce stress',
    icon: <Sparkles className="w-6 h-6" />,
    color: 'from-purple-500/40 to-blue-500/40',
    duration: '2-5 min',
    component: QuickFix,
    ambientColor: 'from-purple-500/5 via-blue-400/5 to-transparent'
  },
  {
    id: 'sound',
    name: 'Sound Therapy',
    description: 'Calming soundscapes and ambient noise',
    icon: <Music className="w-6 h-6" />,
    color: 'from-blue-500/40 to-cyan-500/40',
    duration: '10-30 min',
    component: SoundTherapy,
    ambientColor: 'from-blue-500/5 via-cyan-400/5 to-transparent'
  },
  {
    id: 'guided',
    name: 'Guided Experience',
    description: 'Voice-guided meditation and relaxation',
    icon: <Cloud className="w-6 h-6" />,
    color: 'from-cyan-500/40 to-teal-500/40',
    duration: '10-20 min',
    component: GuidedExperience,
    ambientColor: 'from-cyan-500/5 via-teal-400/5 to-transparent'
  }
];

export function MindfulnessPage() {
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null);
  const metrics = useHealthMetrics();

  const ExerciseComponent = selectedExercise 
    ? exercises.find(e => e.id === selectedExercise)?.component 
    : null;

  if (ExerciseComponent) {
    return (
      <ExerciseComponent onClose={() => setSelectedExercise(null)} />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Enhanced Ethereal Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Primary Gradient Layer */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-purple-400/5 to-background animate-pulse" style={{ animationDuration: '8s' }} />
        
        {/* Secondary Ambient Layer */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_var(--tw-gradient-stops))] from-blue-500/10 via-cyan-500/5 to-transparent animate-pulse" style={{ animationDuration: '12s', animationDelay: '1s' }} />
        
        {/* Ethereal Orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-primary/5 blur-3xl animate-pulse" style={{ animationDuration: '10s' }} />
        <div className="absolute bottom-1/3 right-1/3 w-96 h-96 rounded-full bg-purple-500/5 blur-3xl animate-pulse" style={{ animationDuration: '15s' }} />
        
        {/* Final Blur Layer */}
        <div className="absolute inset-0 backdrop-blur-[100px]" />
      </div>

      <div className="relative pt-20 pb-24 container mx-auto px-4 space-y-8">
        {/* Stress Level Card with Enhanced Design */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="theme-card p-8 relative overflow-hidden backdrop-blur-xl border-primary/10"
        >
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-transparent opacity-50" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/10 to-purple-500/10 blur-3xl rounded-full animate-pulse" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-500/10 to-purple-500/10 blur-2xl rounded-full animate-pulse" />
          </div>
          
          <div className="relative space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  className="relative"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Brain className="w-8 h-8 text-primary" />
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                </motion.div>
                <div>
                  <h2 className="text-2xl font-semibold bg-gradient-to-r from-primary via-purple-400 to-blue-500 bg-clip-text text-transparent">
                    Inner Peace
                  </h2>
                  <p className="text-sm text-primary/80">Your mindfulness journey begins here ✨</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent">
                  {metrics.recovery}%
                </div>
                <div className="text-sm text-primary/80">Recovery</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div className="bg-primary/5 backdrop-blur-md p-4 rounded-xl">
                <div className="flex items-center gap-2 text-sm text-primary/80 mb-2">
                  <Heart className="w-4 h-4" />
                  <span>HRV</span>
                </div>
                <div className="text-xl font-semibold">{metrics.hrv.current}ms</div>
              </div>
              <div className="bg-primary/5 backdrop-blur-md p-4 rounded-xl">
                <div className="flex items-center gap-2 text-sm text-primary/80 mb-2">
                  <Activity className="w-4 h-4" />
                  <span>Strain</span>
                </div>
                <div className="text-xl font-semibold">{metrics.strain.current.toFixed(1)}</div>
              </div>
              <div className="bg-primary/5 backdrop-blur-md p-4 rounded-xl">
                <div className="flex items-center gap-2 text-sm text-primary/80 mb-2">
                  <Moon className="w-4 h-4" />
                  <span>Sleep</span>
                </div>
                <div className="text-xl font-semibold">{metrics.sleep.score}%</div>
              </div>
            </div>

            <div className="bg-primary/5 backdrop-blur-md p-6 rounded-xl">
              <div className="flex items-center gap-4">
                <Info className="w-6 h-6 text-primary shrink-0" />
                <p className="text-sm leading-relaxed text-primary/80">
                  {metrics.stress.current > 70 
                    ? "Your mind could use some gentle care. Let's explore some calming practices together. 🌸"
                    : metrics.stress.current > 50
                    ? "A moment of mindfulness can help maintain your balance. Shall we begin? 🍃"
                    : "You're in a wonderful space. Let's nurture this peaceful state. ✨"
                  }
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Exercise Library with Enhanced Design */}
        <div className="space-y-6">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl font-semibold flex items-center gap-3"
          >
            <Sparkles className="w-6 h-6 text-primary" />
            <span className="bg-gradient-to-r from-primary via-purple-400 to-blue-500 bg-clip-text text-transparent">
              Mindfulness Library
            </span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AnimatePresence mode="popLayout">
              {exercises.map((exercise, index) => (
                <motion.button
                  key={exercise.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setSelectedExercise(exercise.id)}
                  className="theme-card relative overflow-hidden group backdrop-blur-xl border-primary/10"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Card Background */}
                  <div className="absolute inset-0">
                    <div className={`absolute inset-0 bg-gradient-to-br ${exercise.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-500" />
                  </div>
                  
                  {/* Content */}
                  <div className="relative p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <motion.div
                          className="relative"
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          {exercise.icon}
                          <div className="absolute inset-0 bg-current opacity-20 blur-lg rounded-full" />
                        </motion.div>
                        <div>
                          <h3 className="text-lg font-medium">{exercise.name}</h3>
                          <p className="text-sm text-primary/80">{exercise.description}</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    <div className="flex items-center gap-6 text-sm text-primary/80">
                      <div className="flex items-center gap-2">
                        <Timer className="w-4 h-4" />
                        <span>{exercise.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Play className="w-4 h-4" />
                        <span>Begin Journey</span>
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}